<!--
  @component TopologyTruthEdge — E1 composition with light effecting.
  composes: TopologyIO + BindingIO + CredentialsIO + ProvenanceIO.
  effects:  validate (synchronous, control-plane-pure) +
            reverifyBinding / rotateCredential (delegate to portals
            — which dispatch through the node's command port).
  purpose:  the operator's "declared vs bound vs stale" view.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { TOPOLOGY_TRUTH_EDGE_KEY } from "./deployment-edges.js";
  import type { TopologyTruthEdgeContext } from "./deployment-edges.js";
  import type {
    LiquidefyTopology,
    TopologyValidation,
  } from "../../nodes/deployment/deployment-io.js";

  export { TOPOLOGY_TRUTH_EDGE_KEY };
  export type { TopologyTruthEdgeContext };

  export function useTopologyTruthEdge(): TopologyTruthEdgeContext {
    const ctx = getContext<TopologyTruthEdgeContext | undefined>(
      TOPOLOGY_TRUTH_EDGE_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useTopologyTruthEdge() called outside of a TopologyTruthEdge tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useTopologyIO } from "../../nodes/deployment/TopologyIO.svelte";
  import { useBindingIO } from "../../nodes/deployment/BindingIO.svelte";
  import { useCredentialsIO } from "../../nodes/deployment/CredentialsIO.svelte";
  import { useProvenanceIO } from "../../nodes/deployment/ProvenanceIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const topology = useTopologyIO();
  const binding = useBindingIO();
  const credentials = useCredentialsIO();
  const provenance = useProvenanceIO();

  let lastValidation: TopologyValidation | null = $state(null);
  let lastError: Error | null = $state(null);

  const ctx: TopologyTruthEdgeContext = {
    get topology() {
      return topology.topology;
    },
    get lastValidation() {
      return lastValidation;
    },
    get lastError() {
      return lastError;
    },
    get bindingsHistory() {
      return provenance.events.filter((e) => e.op.startsWith("binding."));
    },
    get credentialsHistory() {
      return provenance.events.filter((e) =>
        e.op.startsWith("credentials."),
      );
    },
    validate(candidate?: LiquidefyTopology): TopologyValidation {
      const target = candidate ?? topology.topology;
      if (!target) {
        const err = new Error(
          "TopologyTruthEdge.validate: no topology available (parent control surface not ready).",
        );
        lastError = err;
        throw err;
      }
      try {
        const result = topology.validate(target);
        lastValidation = result;
        lastError = null;
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        lastError = err;
        throw err;
      }
    },
    diff(against: LiquidefyTopology): unknown {
      const current = topology.topology;
      if (!current) {
        const err = new Error(
          "TopologyTruthEdge.diff: no current topology (parent control surface not ready).",
        );
        lastError = err;
        throw err;
      }
      return topology.diff(current, against);
    },
    reverifyBinding: (seam) => binding.reverify(seam),
    rotateCredential: (ref, options?) => credentials.rotate(ref, options),
  } satisfies TopologyTruthEdgeContext;

  setContext(TOPOLOGY_TRUTH_EDGE_KEY, ctx);
</script>

{@render children?.()}
